import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { billingAPI } from '../lib/api';
import { planLabel, formatEuros } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, CreditCard, ArrowRight } from 'lucide-react';

const BillingPage = () => {
  const { user, checkAuth } = useAuth();
  const [plans, setPlans] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        billingAPI.plans(),
        billingAPI.subscription(),
      ]);
      setPlans(plansRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 5) return;

    try {
      const { data } = await billingAPI.status(sid);
      if (data.payment_status === 'paid') {
        await checkAuth();
        navigate('/dashboard/billing', { replace: true });
        fetchData();
        return;
      }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch (error) {
      console.error('Failed to check payment status:', error);
    }
  }, [checkAuth, navigate, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId, pollPaymentStatus]);

  const [checkoutError, setCheckoutError] = useState('');

  const handleUpgrade = async (planId) => {
    setCheckoutLoading(planId);
    setCheckoutError('');
    try {
      const { data } = await billingAPI.checkout(planId, window.location.origin);
      window.location.href = data.url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
      setCheckoutError('Le paiement n’a pas pu s’ouvrir. Réessayez, ou écrivez-nous à contact@getloyalpass.fr.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const planFeatures = {
    trial: ['1 carte de fidélité', '1 000 clients', 'Apple Wallet + Google Wallet', 'Scans illimités', 'Toute l’équipe scanne avec le même compte', 'Statistiques', 'Géofencing 1 zone', 'Support email'],
    essentiel: ['1 carte de fidélité', '1 000 clients', 'Apple Wallet + Google Wallet', 'Scans illimités', 'Toute l’équipe scanne avec le même compte', 'Statistiques', 'Notifications push', 'Géofencing 1 zone', 'Support email (lun-ven, <48h)'],
    pro: ['3 cartes de fidélité', '5 000 clients', 'Tout Essentiel +', 'Géofencing jusqu\'à 10 zones', 'Support prioritaire (<24h ouvrées)'],
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentPlan = subscription?.plan || 'essentiel';
  const isTrialing = subscription?.status === 'trialing' || currentPlan === 'trial';
  const trialDaysLeft = subscription?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / 86400000))
    : null;
  // The free trial is not something you can pick; only show it while you are on it.
  const visiblePlans = Object.entries(plans).filter(([planId]) => planId !== 'trial' || planId === currentPlan);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          Abonnement
        </h1>
        <p className="text-slate-600 mt-1">Gérez votre abonnement et votre facturation</p>
      </div>

      {/* Current Plan */}
      <Card className="border-slate-200 border-l-4 border-l-blue-600">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-slate-500 mb-1">Plan actuel</p>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-slate-900">{planLabel(currentPlan)}</h2>
                {isTrialing && currentPlan !== 'trial' && (
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                    Essai
                  </span>
                )}
                {subscription?.status === 'active' && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    Actif
                  </span>
                )}
              </div>
              {isTrialing && subscription?.trial_ends_at && (
                <p className="text-sm text-slate-500 mt-1">
                  {trialDaysLeft === 0
                    ? 'L’essai se termine aujourd’hui'
                    : `Encore ${trialDaysLeft} jour${trialDaysLeft > 1 ? 's' : ''} d’essai (jusqu’au ${new Date(subscription.trial_ends_at).toLocaleDateString('fr-FR')})`}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-slate-900">
                {formatEuros(plans[currentPlan]?.price || 0)}
                <span className="text-base font-normal text-slate-500">/mois</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plans */}
      {checkoutError && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3" role="alert">{checkoutError}</p>
      )}

      <div className={`grid gap-6 ${visiblePlans.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {visiblePlans.map(([planId, plan]) => {
          const isCurrentPlan = planId === currentPlan;
          const features = planFeatures[planId] || [];

          return (
            <Card 
              key={planId} 
              className={`border-slate-200 relative ${isCurrentPlan ? 'ring-2 ring-blue-600' : ''}`}
              data-testid={`plan-${planId}`}
            >
              {planId === 'pro' && !isCurrentPlan && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Le plus populaire
                </div>
              )}
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{planLabel(planId)}</span>
                  {isCurrentPlan && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Actuel</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <span className="text-4xl font-black text-slate-900">{formatEuros(plan.price)}</span>
                  <span className="text-slate-500">/mois</span>
                </div>

                <ul className="space-y-3 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <Button variant="outline" className="w-full" disabled>
                    Plan actuel
                  </Button>
                ) : (
                  <Button 
                    className={`w-full ${planId === 'pro' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    variant={planId === 'pro' ? 'default' : 'outline'}
                    onClick={() => handleUpgrade(planId)}
                    disabled={checkoutLoading === planId}
                    data-testid={`upgrade-${planId}`}
                  >
                    {checkoutLoading === planId ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ArrowRight className="w-4 h-4 mr-2" />
                    )}
                    {isTrialing ? `Choisir ${planLabel(planId)}` : `Passer à ${planLabel(planId)}`}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Usage */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Utilisation et limites</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500 mb-2">Cartes de fidélité</p>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 rounded-full"
                  style={{ width: `${Math.min(100, (user?.total_cards || 0) / plans[currentPlan]?.max_cards * 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {user?.total_cards || 0} / {plans[currentPlan]?.max_cards === 999 ? 'Illimité' : plans[currentPlan]?.max_cards}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-2">Clients</p>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-600 rounded-full"
                  style={{ width: `${Math.min(100, (user?.total_customers || 0) / plans[currentPlan]?.max_customers * 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {user?.total_customers || 0} / {plans[currentPlan]?.max_customers > 100000 ? 'Illimité' : plans[currentPlan]?.max_customers?.toLocaleString('fr-FR')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Info */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Informations de paiement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600">
            Les paiements sont traités de manière sécurisée via Stripe. Vous pouvez gérer vos moyens de paiement et consulter vos factures dans le portail client Stripe.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingPage;
